import { useDispatch, useSelector } from "react-redux";
import {
  selectBalanceUsageDetails,
  selectBalanceUsageDetailsLoading,
  selectServiceUsagePrices,
} from "../../../redux/tariffs/selectors.js";
import css from "./BalanceUsageModal.module.css";
import { useEffect, useState } from "react";
import {
  getBalanceUsageDetails,
  getServicesUsagePrices,
} from "../../../redux/tariffs/operations.js";
import { BsXLg } from "react-icons/bs";
import LoaderSvg from "../../Loader/LoaderSvg.jsx";
import CalendarPeriodSelector from "../../sharedComponents/CalendarPeriodSelector/CalendarPeriodSelector.jsx";
import { format } from "date-fns";
import { buildPeriodFromFilter } from "../../../utils/calendarPeriod/buildPeriodFromFilter.js";

const STATUS_LABELS = {
  success: "Успішно",
  failed: "Помилка",
  pending: "В обробці",
};

function formatDate(dateStr) {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleString("uk-UA", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatPhone(phone) {
  if (!phone) return "—";
  return String(phone);
}

const CURRENCY_SYMBOLS = {
  UAH: "₴",
  USD: "$",
  EUR: "€",
};

function currencySymbol(code) {
  return CURRENCY_SYMBOLS[code?.toUpperCase()] ?? code ?? "";
}

export default function BalanceUsageModal({
  onClose,
  selectedService,
  onOpenTopup,
}) {
  const dispatch = useDispatch();
  const balanceUsageDetails = useSelector(selectBalanceUsageDetails);
  const serviceUsagePrices = useSelector(selectServiceUsagePrices);
  const isLoading = useSelector(selectBalanceUsageDetailsLoading);

  const defaultPeriod = buildPeriodFromFilter({
    filter: "week",
    serviceCreatedAt: selectedService?.created_at,
  });

  const [dateFrom, setDateFrom] = useState(defaultPeriod.start);
  const [dateTo, setDateTo] = useState(defaultPeriod.end);

  useEffect(() => {
    dispatch(getServicesUsagePrices());
  }, [dispatch]);

  useEffect(() => {
    dispatch(
      getBalanceUsageDetails({
        workshopId: selectedService.id,
        date_from: dateFrom ? format(dateFrom, "yyyy-MM-dd") : undefined,
        date_to: dateTo ? format(dateTo, "yyyy-MM-dd") : undefined,
      })
    );
  }, [dispatch, selectedService.id, dateFrom, dateTo]);

  // Будуємо словник service_type -> description з serviceUsagePrices
  const serviceTypeLabels =
    serviceUsagePrices?.reduce((acc, item) => {
      acc[item.service_type] = item.description;
      return acc;
    }, {}) ?? {};

  return (
    <div className={css.modal}>
      <BsXLg className={css.closeIcon} onClick={onClose} />

      <h2 className={css.title}>Деталі використання балансу</h2>
      <p className={css.subtitle}>
        {selectedService?.name}
        {selectedService?.balance !== undefined && (
          <span className={css.balance}>
            {" "}
            · Баланс:{" "}
            <strong>
              {selectedService.balance}{" "}
              {currencySymbol(selectedService.balance_currency || "UAH")}
            </strong>
          </span>
        )}
      </p>

      {/* Фільтр по датах */}
      <div className={css.filterRow}>
        <span className={css.filterLabel}>Період:</span>
        <CalendarPeriodSelector
          renderInModal
          periodStartData={dateFrom}
          periodEndData={dateTo}
          handleInputChangeBeg={setDateFrom}
          handleInputChangeEnd={setDateTo}
          noFuture
        />
      </div>

      {/* Прайс послуг */}
      {serviceUsagePrices && serviceUsagePrices.length > 0 && (
        <div className={css.priceList}>
          <p className={css.priceListTitle}>Вартість послуг:</p>
          <div className={css.priceItems}>
            {serviceUsagePrices
              .filter(item => item.service_type === 'sms')
              .map(item => (
              <div key={item.id} className={css.priceItem}>
                <span className={css.priceItemName}>{item.description}</span>
                <span className={css.priceItemValue}>
                  {parseFloat(item.unit_price).toFixed(2)}{" "}
                  {currencySymbol(item.currency)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className={css.tableWrapper}>
        {isLoading ? (
          <LoaderSvg />
        ) : !balanceUsageDetails || balanceUsageDetails?.length === 0 ? (
          <p className={css.empty}>Немає даних про використання балансу</p>
        ) : (
          <div className={css.tableScroll}>
            <table className={css.table}>
              <thead>
                <tr>
                  <th>Послуга</th>
                  <th>Телефон</th>
                  <th>Статус</th>
                  <th>Кількість</th>
                  <th>Ціна/од.</th>
                  <th>Сума</th>
                  <th>Дата</th>
                </tr>
              </thead>
              <tbody>
                {balanceUsageDetails?.map(row => (
                  <tr key={row.id}>
                    <td>
                      {serviceTypeLabels[row.service_type] ?? row.service_type}
                    </td>
                    <td>{formatPhone(row.event_data?.phone)}</td>
                    <td>
                      <span
                        className={
                          css[`status_${row.status}`] || css.statusDefault
                        }
                      >
                        {STATUS_LABELS[row.status] ?? row.status}
                      </span>
                    </td>
                    <td>{row.units}</td>
                    <td>{parseFloat(row.unit_price).toFixed(2)}</td>
                    <td>
                      {parseFloat(row.total_cost).toFixed(2)}{" "}
                      {currencySymbol(row.currency)}
                    </td>
                    <td>{formatDate(row.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className={css.footer}>
        <button className={css.topupBtn} onClick={onOpenTopup}>
          Поповнити баланс
        </button>
      </div>
    </div>
  );
}
